import {PageContainer} from '@/components/organisms'
import {useMemo, useState, useRef} from 'react'
import {useIntl} from 'react-intl'
import {MoveStoresModal} from '@/containers/ClubPlus/Tabs/TierTab/MoveStoresModal'
import {format} from 'date-fns'
import {useFileUploadModal} from '@/containers/ClubPlus/Tabs/Overview/TierFileUploadModal/useFileUploadModal'
import {TierFileUploadModal} from '@/containers/ClubPlus/Tabs/Overview/TierFileUploadModal/TierFileUploadModal'
import './_tierTab.scss'
import { useTiers } from '@/hooks/useTiers'
import CustomDataTable from '@/components/organisms/CustomDataTable/CustomDataTable'
import { getCellDataMappedCols } from '@/helpers/dataTableCellDataMapper'

const cellDataMapperObj = ( ) => ({
  storeId: {
    cellData: ctx => {
          const storeId = ctx?.row?.original?.storeId
          return <span className="ml-4">{storeId}</span>
        },
  },
  lastUpdated: {
    cellData: ({ row: { original } = {} }) => <div>{original?.lastUpdated ?? original?.displayDate ?? '—'}</div>,
  }
});

export const TierTab = ({tierID, tierName}) => {
  const [selectedStores, setSelectedStores] = useState([])
  const [tableResetKey, setTableResetKey] = useState(0)
  const [isSelectAll, setIsSelectAll] = useState(false)

  let pageSize = 10
  const {
      tiersData,
      TierColConfigData,
      pageIndex,
      setPageIndex,
      setSortingObj,
      sortingObj,
      setSearchArry,
      handleResetAll,
      isLoading,
      refetch
    } = useTiers(tierID, pageSize)

  const {formatMessage} = useIntl()
  const tableRef = useRef(null)

  const [moveStoresModalOpen, setMoveStoresModalOpen] = useState(false)
  const {isFileUploadModalOpen, setIsFileUploadModalOpen, selectedTierInfo, handleModalOpen, formData, setFormData} =
    useFileUploadModal()
  const {id} = selectedTierInfo

  const handleRefetchWithCleanup = () => {
    setSelectedStores([])
    setTableResetKey(prevKey => prevKey + 1)

    return refetch().then(() => {
        setSelectedStores([])
    })
  }

  const handleMoveStoresModalClose = () => {
    setMoveStoresModalOpen(false)
    handleRefetchWithCleanup()
  }

  const stores = useMemo(() => {
    if (!tiersData) return []

    return (tiersData?.stores || []).map(store => {
      let displayDate = '-'

      if (store.lastModifiedDate) {
        const date = new Date(store.lastModifiedDate)
        displayDate = format(date, 'yyyy-MM-dd')
      }

      return {
        ...store,
        displayDate,
      }
    })
  }, [tiersData])

  const transformSelectedStores = ( stores ) => {
    if (!stores) return []

    const transformedStores = stores.map(store => store.storeId)
    return transformedStores
  }

  const pagination = useMemo(() => {
    if (!tiersData) return 0
    return tiersData?.pagination
  }, [tiersData])

  const cellDataMappedCols = getCellDataMappedCols(cellDataMapperObj(), TierColConfigData)


  return (
    <PageContainer key={tierID}>
      <div className="stores-table">

        <CustomDataTable
          key={`tier-table-${tableResetKey}`}
          ref={tableRef}
          columns={cellDataMappedCols}
          records={stores}
          isLoading={isLoading}
          pageSize={pageSize}
          setPageIndex={setPageIndex}
          handleSorting={setSortingObj}
          setSearchArry={setSearchArry}
          onResetAll={handleResetAll}
          onRowSelect={(selectedRecords, isAll) => {
            setSelectedStores(transformSelectedStores(selectedRecords))
            setIsSelectAll(isAll)
          }}
          isSelectAll={isSelectAll}
          setIsSelectAll={setIsSelectAll}
          showDownload={false}
          showSelectionSummary={true}
          pageIndex={pageIndex}
          initialSortConfig={sortingObj}
          showAddStores={true}
          onAddStores={() => {handleModalOpen(tierID, tierName)
          }}
          showMoveStores={true}
          onMoveStores={() => {setMoveStoresModalOpen(true)
          }}
          tierId={tierID}
          tierName={tierName}
          totalRecords={pagination?.totalStores}
          firstPageIndex={1}
        />
        <div className='total-records' data-testid='total-records'>
          {formatMessage({id: 'CLUB_PLUS.TIER_LIST_TABLE_TOTAL_RECORDS'}, {totalRecords: pagination?.totalStores || 0})}
        </div>
      </div>

      <TierFileUploadModal
        isVisible={isFileUploadModalOpen}
        setIsVisible={setIsFileUploadModalOpen}
        onSuccess={handleRefetchWithCleanup}
        formData={formData}
        setFormData={setFormData}
        tierID={id}
        tierName={tierName}
      />

      <MoveStoresModal
        isOpen={moveStoresModalOpen}
        closeModal={handleMoveStoresModalClose}
        selectedStores={selectedStores}
        tierName={tierName}
        tierID={tierID}
        onSuccess={handleRefetchWithCleanup}
      />
    </PageContainer>
  )
}
